import { type FetchBaseQueryError, skipToken } from '@reduxjs/toolkit/query';
import { USStateOptions } from '@strata/constants';
import { Button } from '@strata/react-buttons';
import { PhoneInputFormik, TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { useFormikContext } from 'formik';
import { type ComponentProps, useState } from 'react';
import type * as Yup from 'yup';

import { PrescriberLookupDialog } from 'components/intakeForm/dialogs/PrescriberLookupDialog';
import { ActionPlan } from 'services/constants';
import { handleError } from 'services/Error';
import { useGetPrescriberPreferenceQuery } from 'store/api/intakeCaseApi';
import {
    type PracticeDetails,
    useLazyGetPracticeDetailsQuery,
    useLazyGetPrescriberLookupQuery,
} from 'store/api/referenceApi';
import {
    type FormSections,
    type InitialIntakeFormValues,
    type IntakeFormSectionSchemas,
} from 'store/intakeForm/intakeFormSchema';

type Section = typeof FormSections.PRESCRIBER;

interface PrescriberInputsProps {
    disabled?: boolean;
    prescriberNpiRef?: ComponentProps<typeof TextInputFormik>['inputRef'];
}

const PrescriberPreferenceOptions = [
    { name: ActionPlan.FMH_TO_SUBMIT },
    { name: ActionPlan.PRESCRIBER_TO_SUBMIT },
    { name: ActionPlan.PHARMACY_TO_SUBMIT },
];

export const PrescriberInputs = ({
    disabled = false,
    prescriberNpiRef = undefined,
}: PrescriberInputsProps) => {
    const formik = useFormikContext<Yup.InferType<(typeof IntakeFormSectionSchemas)[Section]>>();

    const [showPrescriberLookupDialog, setShowPrescriberLookupDialog] = useState(false);
    const [
        getPrescriberLookup,
        {
            data: prescriberLookupResponse,
            isFetching: prescriberLookupFetching,
            error: prescriberLookupError,
        },
    ] = useLazyGetPrescriberLookupQuery();
    const [getPracticeDetails] = useLazyGetPracticeDetailsQuery();

    const { data: prescriberPreference } = useGetPrescriberPreferenceQuery(
        formik.values.prescriberNpi ? { prescriberNpi: formik.values.prescriberNpi } : skipToken,
    );

    return (
        <div className="sds_intake-form-grid">
            <PrescriberLookupDialog
                isOpen={showPrescriberLookupDialog}
                prescriberLookupResponse={!prescriberLookupError ? prescriberLookupResponse : []}
                enteredPrescriberNpi={formik.values.prescriberNpi}
                onSecondaryButtonClick={() => setShowPrescriberLookupDialog(false)}
                onPrimaryButtonClick={(selectedPrescriber) => {
                    // eslint-disable-next-line complexity
                    void (async () => {
                        setShowPrescriberLookupDialog(false);

                        const prescriberPracticeNpi = selectedPrescriber?.practiceNpi;
                        let practice: PracticeDetails | undefined;
                        try {
                            practice = prescriberPracticeNpi
                                ? await getPracticeDetails(prescriberPracticeNpi).unwrap()
                                : undefined;
                        } catch (err) {
                            if ((err as FetchBaseQueryError).status !== 404) {
                                handleError(
                                    'An error occurred during prescriber lookup.',
                                    err as FetchBaseQueryError,
                                );
                            }
                        }

                        await formik.setValues({
                            ...formik.values,
                            // Use selected prescriber values
                            prescriberNpi: selectedPrescriber
                                ? selectedPrescriber?.prescriberNpi
                                : formik.values.prescriberNpi,
                            prescriberFirstName: selectedPrescriber
                                ? selectedPrescriber?.firstName
                                : formik.values.prescriberFirstName,
                            prescriberLastName: selectedPrescriber
                                ? selectedPrescriber?.lastName
                                : formik.values.prescriberLastName,
                            prescriberPhoneNumber: selectedPrescriber?.telephoneNumber,
                            prescriberFaxNumber: selectedPrescriber?.faxNumber,
                            prescriberAddress1: selectedPrescriber?.address1,
                            prescriberAddress2: selectedPrescriber?.address2,
                            prescriberCity: selectedPrescriber?.city,
                            prescriberState: selectedPrescriber?.state,
                            prescriberZip: selectedPrescriber?.zip,
                            prescriberPracticeNpi,
                            prescriberPracticeName: practice?.practiceName,
                            prescriberPracticePhoneNumber: practice?.telephoneNumber,
                            prescriberPracticeFaxNumber: practice?.faxNumber,
                            prescriberPracticeAddress1: practice?.address1,
                            prescriberPracticeAddress2: practice?.address2,
                            prescriberPracticeCity: practice?.city,
                            prescriberPracticeState: practice?.state,
                            prescriberPracticeZip: practice?.zip,
                            prescriberVerified: !!selectedPrescriber,
                        } as (typeof InitialIntakeFormValues)[Section]);
                    })();
                }}
            />
            <div className="sds_intake-form-section__row">
                <div style={{ gridColumn: '1', minWidth: 0 }}>
                    <TextInputFormik
                        id="prescriberNpi"
                        label="Prescriber NPI"
                        required
                        inputRef={prescriberNpiRef}
                        disabled={disabled || formik.values.prescriberVerified != null}
                    />
                </div>
                {!disabled &&
                    (formik.values.prescriberVerified == null ? (
                        <div style={{ gridColumn: '2 / -1', minWidth: 0 }}>
                            <Button
                                className="margin__top--large"
                                label="Lookup Prescriber"
                                id="lookup-prescriber-button"
                                intent={Button.Intent.PRIMARY}
                                disabled={!!formik.errors.prescriberNpi || prescriberLookupFetching}
                                onClick={() => {
                                    void (async () => {
                                        try {
                                            await getPrescriberLookup(formik.values).unwrap();
                                            setShowPrescriberLookupDialog(true);
                                        } catch (err) {
                                            if ((err as FetchBaseQueryError).status === 404) {
                                                setShowPrescriberLookupDialog(true);
                                            } else {
                                                handleError(
                                                    'An error occurred during prescriber lookup.',
                                                    err as FetchBaseQueryError,
                                                );
                                            }
                                        }
                                    })();
                                }}
                            />
                        </div>
                    ) : (
                        <div style={{ gridColumn: '2 / -1', minWidth: 0 }}>
                            <Button
                                className="margin__top--large"
                                label="Reset"
                                id="reset-prescriber-info-button"
                                onClick={() => {
                                    void Promise.all(
                                        [
                                            'prescriberNpi',
                                            'prescriberFirstName',
                                            'prescriberLastName',
                                            'prescriberPhoneNumber',
                                            'prescriberFaxNumber',
                                            'prescriberAddress1',
                                            'prescriberAddress2',
                                            'prescriberCity',
                                            'prescriberState',
                                            'prescriberZip',
                                            'prescriberPracticeNpi',
                                            'prescriberPracticeName',
                                            'prescriberPracticePhoneNumber',
                                            'prescriberPracticeFaxNumber',
                                            'prescriberPracticeAddress1',
                                            'prescriberPracticeAddress2',
                                            'prescriberPracticeCity',
                                            'prescriberPracticeState',
                                            'prescriberPracticeZip',
                                            'prescriberVerified',
                                        ].map((field) => formik.setFieldValue(field, undefined)),
                                    );
                                }}
                            />
                        </div>
                    ))}
            </div>
            {formik.values.prescriberVerified != null && (
                <>
                    <div className="sds_intake-form-section__row">
                        <div style={{ gridColumn: '1', minWidth: 0 }}>
                            <TextInputFormik
                                id="prescriberFirstName"
                                label="Prescriber First Name"
                                required
                                disabled={disabled || formik.values.prescriberVerified === true}
                            />
                        </div>
                        <div style={{ gridColumn: '2 / -1', minWidth: 0 }}>
                            <TextInputFormik
                                id="prescriberLastName"
                                label="Prescriber Last Name"
                                required
                                disabled={disabled || formik.values.prescriberVerified === true}
                            />
                        </div>
                    </div>
                    <div className="sds_intake-form-section__row">
                        <div style={{ gridColumn: '1', minWidth: 0 }}>
                            <PhoneInputFormik
                                id="prescriberPhoneNumber"
                                label="Phone"
                                required
                                disabled={disabled}
                                inputCopyButton
                            />
                        </div>
                        <div style={{ gridColumn: '2 / -1', minWidth: 0 }}>
                            <PhoneInputFormik
                                id="prescriberFaxNumber"
                                label="Fax"
                                required
                                disabled={disabled}
                                inputCopyButton
                            />
                        </div>
                    </div>
                    <div className="sds_intake-form-section__row">
                        <div style={{ gridColumn: '1 / -1', minWidth: 0 }}>
                            <TextInputFormik
                                id="prescriberAddress1"
                                label="Address Line 1"
                                required
                                disabled={disabled}
                                inputCopyButton
                            />
                        </div>
                    </div>
                    <div className="sds_intake-form-section__row">
                        <div style={{ gridColumn: '1 / -1', minWidth: 0 }}>
                            <TextInputFormik
                                id="prescriberAddress2"
                                label="Address Line 2"
                                disabled={disabled}
                            />
                        </div>
                    </div>
                    <div className="sds_intake-form-section__row">
                        <div style={{ gridColumn: '1', minWidth: 0 }}>
                            <TextInputFormik
                                id="prescriberCity"
                                label="City"
                                required
                                disabled={disabled}
                            />
                        </div>
                        <div style={{ gridColumn: '2', minWidth: 0 }}>
                            <SuggestSingleSelectFormik
                                id="prescriberState"
                                label="State"
                                required
                                disabled={disabled}
                                values={USStateOptions}
                                onChange={(newVal) => {
                                    void formik.setFieldValue(
                                        'prescriberState',
                                        newVal?.secondaryData,
                                    );
                                }}
                                selectedItem={USStateOptions.find(
                                    ({ secondaryData }) =>
                                        secondaryData === formik.values.prescriberState,
                                )}
                            />
                        </div>
                        <div style={{ gridColumn: '3', minWidth: 0 }}>
                            <TextInputFormik
                                id="prescriberZip"
                                label="ZIP Code"
                                required
                                disabled={disabled}
                            />
                        </div>
                    </div>
                    <div className="sds_intake-form-section__row">
                        <div style={{ gridColumn: '1', minWidth: 0 }}>
                            <SuggestSingleSelectFormik
                                id="prescriberPref"
                                label="Prescriber Preference"
                                disabled={disabled}
                                values={PrescriberPreferenceOptions}
                                onChange={(newVal) => {
                                    void formik.setFieldValue('prescriberPref', newVal?.name);
                                }}
                                selectedItem={PrescriberPreferenceOptions.find(
                                    ({ name }) => name === prescriberPreference,
                                )}
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
